import { apiClient, isFeatureEnabled } from "./authService";
import { unwrapItem, unwrapList } from "../utils/apiResponse";
import { ClientPackage } from "./PackagesService";

export type EnrollmentMode = "FIXED_ROSTER" | "DROP_IN" | "BOTH";
export type RecurrenceFrequency = "DAILY" | "WEEKLY" | "MONTHLY";

/** A booking can only be opened this far ahead — past it the API returns 400. */
export const BOOKING_HORIZON_DAYS = 366;
/** How far ahead the nightly job turns projected sessions into real rows. */
export const MATERIALIZED_AHEAD_DAYS = 14;
/** Upper bound the API enforces on a single enrollment block. */
export const MAX_ENROLL_SESSIONS = 200;

export interface ClassCategory {
  id: string;
  name?: string;
  description?: string | null;
  active?: boolean;
  [key: string]: any;
}

export interface ClassCoachRef {
  id: string;
  full_name?: string;
  phone?: string | null;
}

export interface ClassDefinition {
  id: string;
  name?: string;
  description?: string | null;
  unit_id?: string;
  unit?: { id: string; name?: string } | null;
  category_id?: string | null;
  category?: ClassCategory | null;
  coach_id?: string;
  coach?: ClassCoachRef | null;
  capacity?: number;
  duration?: number;
  price?: number | string;
  enrollment_mode?: EnrollmentMode;
  active?: boolean;
  upcoming_sessions?: ClassSession[];
  recurrence_frequency?: RecurrenceFrequency | null;
  recurrence_interval?: number | null;
  recurrence_week_days?: number[] | null;
  recurrence_month_day?: number | null;
  recurrence_month_days?: number[] | null;
  recurrence_start?: string | null;
  /** Both null on an open-ended class — it runs until further notice. */
  recurrence_end?: string | null;
  recurrence_count?: number | null;
  [key: string]: any;
}

/**
 * A date on a recurring class is in one of two states and `is_virtual` says
 * which. `false` means a real appointment row exists and `id` is a uuid.
 * `true` means it was projected from the recurrence rule at request time and
 * `id` is `v:<class_id>:<yyyy-mm-dd>` — there is no row behind it yet.
 *
 * Every `:appointmentId` route accepts either form. Reads leave a projected
 * session projected; any write materializes it, which means its id changes.
 * Never hold onto an id across a write — refetch the list instead.
 */
export interface ClassSession {
  id: string;
  start_at?: string;
  end_at?: string;
  status?: string;
  unit?: { id: string; name?: string } | null;
  class?: ClassDefinition | null;
  roster?: RosterEntry[];
  /** Attendees with status BOOKED — cancelled spots are not counted. */
  booked_count?: number;
  /** True while the session is only projected from the rule. */
  is_virtual?: boolean;
  /** `yyyy-mm-dd` of the occurrence this session belongs to. */
  class_occurrence_date?: string | null;
  [key: string]: any;
}

/** `GET /classes/:id/sessions` is a window now, not the whole series. */
export interface ClassSessionsWindow {
  range: { start: string; end: string };
  sessions: ClassSession[];
}

export interface RosterEntry {
  /**
   * `null` on a projected session — no attendance row exists yet, so this is
   * never safe to use as a React key or to address the entry with.
   */
  id: string | null;
  client_id?: string;
  client?: { id?: string; full_name?: string; phone?: string | null } | null;
  status?: string;
  source?: string;
  client_package_id?: string | null;
  invoice_id?: string | null;
  /** True for an enrollment that *will* be on the session once it exists. */
  is_projected?: boolean;
  [key: string]: any;
}

export interface CreateCategoryPayload {
  name: string;
  description?: string;
  active?: boolean;
}

export interface UpdateCategoryPayload {
  name?: string;
  description?: string;
  active?: boolean;
}

/**
 * `start_at`/`end_at` no longer bound the series — they supply the time of day
 * and the anchor date, and nothing projects before that date. `end_at` minus
 * `start_at` must equal `duration` exactly or the request is rejected.
 *
 * Not sent deliberately: `recurrence_start` (defaults to `start_at`'s date) and
 * `recurrence_month_day` (superseded by the plural form).
 */
export interface RecurrencePayload {
  recurrence_frequency?: RecurrenceFrequency;
  recurrence_interval?: number;
  /** WEEKLY only, and required for it — 0 is Sunday. */
  recurrence_week_days?: number[];
  /** MONTHLY only; defaults to `start_at`'s day when omitted. */
  recurrence_month_days?: number[];
  /** Omit both this and `recurrence_count` for an open-ended class. */
  recurrence_end?: string;
  recurrence_count?: number;
}

export interface CreateClassPayload extends RecurrencePayload {
  unit_id: string;
  category_id?: string;
  coach_id: string;
  name: string;
  description?: string;
  capacity: number;
  duration: number;
  price: number;
  enrollment_mode?: EnrollmentMode;
  start_at: string;
  end_at: string;
}

/** Definition only — see `updateClassSchedule` for when the class runs. */
export interface UpdateClassPayload {
  name?: string;
  description?: string;
  category_id?: string;
  coach_id?: string;
  capacity?: number;
  price?: number;
  enrollment_mode?: EnrollmentMode;
  active?: boolean;
}

export interface UpdateSchedulePayload extends RecurrencePayload {
  start_at?: string;
  duration?: number;
}

export interface CreateClassResult {
  class: ClassDefinition;
  /**
   * Empty for a recurring class — nothing is written up front. A non-recurring
   * class still returns its one real session.
   */
  sessions: ClassSession[];
  /** Projected dates, so the form can show what was just created. */
  preview_sessions: ClassSession[];
}

/** One itemised entry from a 409 on create/reschedule. */
export interface ScheduleConflict {
  occurrence_date?: string;
  start_at?: string;
  end_at?: string;
  /** Free text, e.g. `Coach is teaching recurring class "Junior Padel"`. */
  reason?: string;
}

export interface PackageBreakdownEntry {
  client_package_id?: string;
  package_name?: string;
  sessions?: number;
  expires_at?: string | null;
}

/**
 * The clamp fields, shared by the quote and the commit.
 *
 * `sessions_requested` is the **effective** number that was priced, which is
 * what reconciliation must use — `covered_by_package + invoiced_count` adds up
 * to this, never to `sessions_asked`.
 */
export interface EnrollClamp {
  /** What was asked for, before any reduction. */
  sessions_asked?: number;
  /** What the class can still deliver. Large on an open-ended class. */
  sessions_available?: number;
  /** True when the ask was reduced to fit. */
  capped?: boolean;
}

export interface EnrollQuote extends EnrollClamp {
  /** Effective, post-clamp, and the figure the split reconciles against. */
  sessions_requested: number;
  covered_by_package: number;
  package_breakdown: PackageBreakdownEntry[];
  invoiced_count: number;
  unit_price?: number;
  invoice_total?: number;
  /**
   * Shorter than `sessions_requested` when the rule doesn't produce that many
   * dates before the 366-day horizon — worth surfacing before selling a block
   * that can't be fully used.
   */
  next_sessions: ClassSession[];
}

export interface EnrollResult extends EnrollClamp {
  enrollment?: { id: string; status?: string; [key: string]: any };
  /** Effective, post-clamp, and the figure the split reconciles against. */
  sessions_requested?: number;
  covered_by_package?: number;
  package_breakdown?: PackageBreakdownEntry[];
  invoiced_count?: number;
  invoice_id?: string | null;
  /**
   * Only the sessions inside the already-materialized 14-day window. The rest
   * of the block is spent as those dates come around — not a partial failure.
   */
  booked_now?: string[];
}

/** A session that kept its old time because people are booked on it. */
export interface KeptSession {
  id?: string;
  start_at?: string;
  end_at?: string;
  client_ids?: string[];
  clients?: { id?: string; full_name?: string; phone?: string | null }[];
  [key: string]: any;
}

/**
 * A client holding a block on the class whose remaining sessions now follow the
 * new time. These people have **already been messaged** by the backend — the
 * opposite of `kept_sessions`, where nothing is sent. Never merge the two
 * groups: one moved and was told, the other didn't move and wasn't.
 */
export interface AffectedEnrollment extends NotifiedClient {
  enrollment_id?: string;
  /** Sessions still owed on their block, which will run at the new time. */
  sessions_remaining?: number;
}

export interface RescheduleClassResult {
  class?: ClassDefinition;
  /** False when the timing didn't actually move; nothing was sent. */
  schedule_changed?: boolean;
  /** How many clients the backend messaged. 0 when the schedule was unchanged. */
  clients_notified?: number;
  /** Booked sessions left at their old time — nobody is told automatically. */
  kept_sessions: KeptSession[];
  /**
   * Unbooked sessions dropped and re-projected from the new rule. Undefined on
   * a build that predates the field, which is why `deleted_count` is still
   * read as a fallback rather than assumed to be zero.
   */
  released_sessions?: ClassSession[];
  /**
   * Clients whose block follows the new schedule. On a class with no
   * materialized sessions this is the *only* populated group — both
   * `kept_sessions` and `released_sessions` come back empty.
   */
  affected_enrollments: AffectedEnrollment[];
  deleted_count?: number;
  preview_sessions?: ClassSession[];
}

/** One session deactivation took down, with who was on it. */
/**
 * One session deactivation took down. `affected_clients` is per-session detail
 * and stays a bare id list — only the top-level `clients_to_notify` carries
 * whole client objects.
 */
export interface CancelledClassSession {
  appointment_id?: string;
  start_at?: string;
  affected_clients?: string[];
  [key: string]: any;
}

export interface UpdateClassResult {
  class: ClassDefinition;
  /** Populated only by a deactivate; empty on a plain edit. */
  cancelled_sessions: CancelledClassSession[];
  /** Clients the backend has already messaged — see `NotifiedClient`. */
  clients_to_notify: NotifiedClient[];
  /**
   * Only on a reactivate: how many unbooked sessions were freed. Lower than
   * what deactivation cancelled, and the gap is the sessions real people were
   * refunded for — those are not resurrected.
   */
  restored_sessions?: number;
}

/**
 * A client the backend has **already** messaged.
 *
 * The whole notification contract for this module, per the backend, is one
 * rule: **if a response hands you clients, they have been messaged — the sole
 * exception being `kept_sessions`.** Those bookings didn't move during a
 * reschedule, so nothing was sent and they are the only group staff must
 * contact themselves.
 *
 * Everything else notifies automatically: drop-in booking, enrollment,
 * attendance cancelled, session cancelled, end series, deactivate, reschedule
 * (`affected_enrollments`), and the 24h/2h reminders. Report counts in the past
 * tense and never send anything on top.
 *
 * Flat — the client's own fields, not a wrapper around a nested `client`.
 */
export interface NotifiedClient {
  id?: string;
  full_name?: string;
  phone?: string | null;
  country?: string | null;
  [key: string]: any;
}

export interface EndClassResult {
  class?: ClassDefinition;
  end_date?: string;
  cancelled_sessions: ClassSession[];
  /** Real people whose booked session just disappeared — they need telling. */
  clients_to_notify: NotifiedClient[];
}

export interface CancelSessionResult {
  appointment_id?: string;
  status?: string;
  cancelled_attendees?: string[];
  failures?: { client_id: string; reason: string }[];
}

/* ------------------------------ session ids ------------------------------- */

export const VIRTUAL_SESSION_ID_PREFIX = "v:";

/** `v:<class_id>:<yyyy-mm-dd>` — a session projected from the rule. */
export const isVirtualSessionId = (id?: string | null): boolean =>
  String(id ?? "").startsWith(VIRTUAL_SESSION_ID_PREFIX);

export const isVirtualSession = (session?: {
  id?: string | null;
  is_virtual?: boolean;
}): boolean =>
  session?.is_virtual === true || isVirtualSessionId(session?.id);

export const parseVirtualSessionId = (
  id?: string | null
): { classId: string; date: string } | null => {
  const raw = String(id ?? "");
  if (!isVirtualSessionId(raw)) return null;
  const rest = raw.slice(VIRTUAL_SESSION_ID_PREFIX.length);
  const split = rest.lastIndexOf(":");
  if (split <= 0) return null;
  return { classId: rest.slice(0, split), date: rest.slice(split + 1) };
};

/**
 * Session ids are opaque — never validate them as uuids. Colons are legal in a
 * path segment so encoding is optional, but it keeps the request well-formed
 * whatever ends up in the id.
 */
const sessionPath = (appointmentId: string) =>
  encodeURIComponent(String(appointmentId));

/**
 * Pulls the real appointment id out of a write response. A write against a
 * projected session materializes it, so this is the id the session now has.
 *
 * Only keys that unambiguously name an appointment are read — a bare `id` on
 * the body is just as likely to be the attendance row's. Returning null is
 * safe: the `v:` id still resolves to the row it materialized into, so the
 * caller just refetches with what it already had.
 */
export const readMaterializedSessionId = (response: any): string | null => {
  const body = response?.data ?? response;
  const inner = body?.data ?? body;
  const candidate =
    inner?.appointment_id ??
    inner?.appointment?.id ??
    inner?.session?.id ??
    inner?.session_id ??
    inner?.attendance?.appointment_id;
  if (!candidate) return null;
  const id = String(candidate);
  return isVirtualSessionId(id) ? null : id;
};

/**
 * The itemised `conflicts` array from a 409 on create/reschedule. Only the next
 * 90 days are validated — a collision past that is resolved first-come-first-
 * served when someone actually books.
 */
export const readScheduleConflicts = (error: any): ScheduleConflict[] => {
  const body = error?.response?.data;
  const list = body?.conflicts ?? body?.data?.conflicts;
  return Array.isArray(list) ? (list as ScheduleConflict[]) : [];
};

/* ------------------------------- categories ------------------------------- */

export const createClassCategory = async (
  payload: CreateCategoryPayload
): Promise<ClassCategory> => {
  const response = await apiClient.post("/classes/categories", payload);
  return unwrapItem(response.data, "category") as ClassCategory;
};

export const getAllClassCategories = async (): Promise<ClassCategory[]> => {
  if (!isFeatureEnabled("CLASSES")) return [];
  const response = await apiClient.get("/classes/categories");
  return unwrapList(response.data, "categories") as ClassCategory[];
};

export const updateClassCategory = async (
  id: string,
  payload: UpdateCategoryPayload
): Promise<ClassCategory> => {
  const response = await apiClient.patch(`/classes/categories/${id}`, payload);
  return unwrapItem(response.data, "category") as ClassCategory;
};

/* --------------------------------- classes -------------------------------- */

/**
 * A recurring class stores a rule, not sessions — `sessions` comes back empty
 * and `preview_sessions` shows the dates the rule projects. Omitting both
 * `recurrence_end` and `recurrence_count` creates an open-ended class.
 *
 * Rejects with 409 and an itemised `conflicts` array (read it with
 * `readScheduleConflicts`) when the schedule collides inside the next 90 days.
 */
export const createClass = async (
  payload: CreateClassPayload
): Promise<CreateClassResult> => {
  const response = await apiClient.post("/classes", payload);
  const body = (response.data as any)?.data ?? (response.data as any) ?? {};
  return {
    class: (body.class ?? body) as ClassDefinition,
    sessions: Array.isArray(body.sessions) ? body.sessions : [],
    preview_sessions: Array.isArray(body.preview_sessions)
      ? body.preview_sessions
      : [],
  };
};

export const getAllClasses = async (
  unitId?: string | null
): Promise<ClassDefinition[]> => {
  const response = await apiClient.get("/classes", {
    params: { unit_id: unitId || undefined },
  });
  return unwrapList(response.data, "classes") as ClassDefinition[];
};

export const getClassById = async (id: string): Promise<ClassDefinition> => {
  const response = await apiClient.get(`/classes/${id}`);
  return unwrapItem(response.data, "class") as ClassDefinition;
};

/**
 * The class must already be ended with no upcoming sessions — the server
 * 400s with a clear message ("end the class first" / "cancel upcoming
 * sessions first") otherwise, meant to be surfaced to the admin verbatim.
 * After this, `GET /classes` simply omits the row; there's no extra flag to
 * filter on the frontend.
 */
export const deleteClass = async (id: string): Promise<ClassDefinition> => {
  const response = await apiClient.delete(`/classes/${id}`);
  return unwrapItem(response.data, "class") as ClassDefinition;
};

/**
 * Name, price, capacity and enrollment mode — never the schedule.
 *
 * Flipping `active` is the exception: it is no longer a harmless toggle.
 * Deactivating cancels every future materialized session — refunding package
 * credits, returning enrollment seats, voiding invoices and messaging each
 * attendee — and reports what it did in `cancelled_sessions` /
 * `clients_to_notify`. Reactivating frees the sessions nobody had booked and
 * counts them in `restored_sessions`; the ones people *were* booked on stay
 * cancelled, since those clients have already been refunded.
 *
 * A plain edit returns the class row alone, with none of those keys set.
 */
export const updateClass = async (
  id: string,
  payload: UpdateClassPayload
): Promise<UpdateClassResult> => {
  const response = await apiClient.patch(`/classes/${id}`, payload);
  const body = (response.data as any)?.data ?? (response.data as any) ?? {};
  return {
    class: (body.class ?? body) as ClassDefinition,
    cancelled_sessions: Array.isArray(body.cancelled_sessions)
      ? body.cancelled_sessions
      : [],
    clients_to_notify: Array.isArray(body.clients_to_notify)
      ? body.clients_to_notify
      : [],
    restored_sessions:
      typeof body.restored_sessions === "number"
        ? body.restored_sessions
        : undefined,
  };
};

/**
 * Changes when the class runs, from today forward. Future sessions nobody
 * booked are deleted and re-projected; sessions with people on them keep their
 * old time and come back in `kept_sessions` — nothing notifies those clients.
 */
export const updateClassSchedule = async (
  id: string,
  payload: UpdateSchedulePayload
): Promise<RescheduleClassResult> => {
  const response = await apiClient.patch(`/classes/${id}/schedule`, payload);
  const body = (response.data as any)?.data ?? (response.data as any) ?? {};
  return {
    class: body.class ?? undefined,
    schedule_changed: body.schedule_changed,
    clients_notified:
      typeof body.clients_notified === "number" ? body.clients_notified : undefined,
    kept_sessions: Array.isArray(body.kept_sessions) ? body.kept_sessions : [],
    released_sessions: Array.isArray(body.released_sessions)
      ? body.released_sessions
      : undefined,
    affected_enrollments: Array.isArray(body.affected_enrollments)
      ? body.affected_enrollments
      : [],
    deleted_count: body.deleted_count,
    preview_sessions: Array.isArray(body.preview_sessions)
      ? body.preview_sessions
      : [],
  };
};

/**
 * Ends an open-ended class as of `endDate` (defaults to today). Anything
 * already materialized past that date is cancelled properly — package credits
 * refunded, seats returned, invoices voided.
 */
export const endClass = async (
  id: string,
  endDate?: string
): Promise<EndClassResult> => {
  const response = await apiClient.patch(
    `/classes/${id}/end`,
    endDate ? { end_date: endDate } : {}
  );
  const body = (response.data as any)?.data ?? (response.data as any) ?? {};
  return {
    class: body.class ?? undefined,
    end_date: body.end_date,
    cancelled_sessions: Array.isArray(body.cancelled_sessions)
      ? body.cancelled_sessions
      : [],
    clients_to_notify: Array.isArray(body.clients_to_notify)
      ? body.clients_to_notify
      : [],
  };
};

/**
 * A window of the series, not the whole thing — an open-ended class has no
 * "every session". Omitting both bounds gives the current month. Sessions past
 * the materialized window come back projected (`is_virtual: true`).
 */
export const getClassSessions = async (
  id: string,
  range?: { start?: string; end?: string }
): Promise<ClassSessionsWindow> => {
  const response = await apiClient.get(`/classes/${id}/sessions`, {
    params: {
      start: range?.start || undefined,
      end: range?.end || undefined,
    },
  });
  const body = (response.data as any)?.data ?? (response.data as any) ?? {};
  return {
    range: body.range ?? { start: range?.start ?? "", end: range?.end ?? "" },
    sessions: unwrapList(body, "sessions") as ClassSession[],
  };
};

/** Session + class definition + full roster in one round trip. */
export const getSessionDetail = async (
  appointmentId: string
): Promise<ClassSession> => {
  const response = await apiClient.get(
    `/classes/sessions/${sessionPath(appointmentId)}`
  );
  return unwrapItem(response.data, "session") as ClassSession;
};

/* --------------------------- enrollment & booking -------------------------- */

export interface EnrollPayload {
  clientId: string;
  /** Required, 1–200 — an open-ended class has no series length to charge. */
  sessions: number;
  clientPackageId?: string;
  forceDropIn?: boolean;
}

const buildEnrollBody = ({
  clientId,
  sessions,
  clientPackageId,
  forceDropIn,
}: EnrollPayload) => ({
  client_id: clientId,
  sessions,
  ...(clientPackageId ? { client_package_id: clientPackageId } : {}),
  ...(forceDropIn ? { use_package: false } : {}),
});

/**
 * Prices a block without committing anything — the screen staff confirms
 * against ("8 sessions — 5 from the package, 3 to pay").
 */
export const quoteEnrollment = async (
  classId: string,
  payload: EnrollPayload
): Promise<EnrollQuote> => {
  const response = await apiClient.post(
    `/classes/${classId}/enroll/quote`,
    buildEnrollBody(payload)
  );
  const body = (response.data as any)?.data ?? (response.data as any) ?? {};
  return {
    sessions_requested: Number(body.sessions_requested ?? payload.sessions),
    sessions_asked: body.sessions_asked,
    sessions_available: body.sessions_available,
    capped: body.capped === true,
    covered_by_package: Number(body.covered_by_package ?? 0),
    package_breakdown: Array.isArray(body.package_breakdown)
      ? body.package_breakdown
      : [],
    invoiced_count: Number(body.invoiced_count ?? 0),
    unit_price: body.unit_price,
    invoice_total: body.invoice_total,
    next_sessions: Array.isArray(body.next_sessions) ? body.next_sessions : [],
  };
};

/** Stable `code` values on class errors — message text is free to change. */
export const CLASS_ERROR_CODES = {
  /** 400 — the class has no upcoming sessions at all. */
  NO_SESSIONS_LEFT: "CLASS_NO_SESSIONS_LEFT",
  /** 400 — this client already holds every remaining session. */
  CLIENT_FULLY_ENROLLED: "CLIENT_FULLY_ENROLLED",
  /** 409 — create or reschedule collides inside the 90-day window. */
  SCHEDULE_CONFLICT: "CLASS_SCHEDULE_CONFLICT",
} as const;

export const readErrorCode = (error: any): string =>
  String(error?.response?.data?.code ?? "").toUpperCase();

/**
 * Wording kept only as a fallback for a backend older than the `code` field.
 * The code is authoritative.
 */
const BLOCKING_ENROLL_MESSAGES = [
  "no upcoming sessions left",
  "already enrolled for every remaining session",
];

/**
 * A 400 meaning this client/class pairing can't be enrolled at all, rather than
 * that this particular request was wrong — the enroll action should be disabled
 * rather than retried with different numbers.
 */
export const isEnrollBlockedError = (error: any): boolean => {
  const code = readErrorCode(error);
  if (
    code === CLASS_ERROR_CODES.NO_SESSIONS_LEFT ||
    code === CLASS_ERROR_CODES.CLIENT_FULLY_ENROLLED
  ) {
    return true;
  }
  const message = String(
    error?.response?.data?.message ?? error?.message ?? ""
  ).toLowerCase();
  return BLOCKING_ENROLL_MESSAGES.some((fragment) => message.includes(fragment));
};

/** `sessions_available` rides along on the blocking 400s — 0 on both. */
export const readSessionsAvailable = (error: any): number | null => {
  const value = error?.response?.data?.sessions_available;
  return typeof value === "number" ? value : null;
};

/**
 * True for the 409 raised when a schedule collides, whether or not the body
 * itemised which dates — so a conflict without a `conflicts` array still opens
 * the conflicts dialog rather than degrading to a bare toast.
 */
export const isScheduleConflictError = (error: any): boolean =>
  readErrorCode(error) === CLASS_ERROR_CODES.SCHEDULE_CONFLICT ||
  readScheduleConflicts(error).length > 0;

/**
 * Buys a block of N sessions. `booked_now` only covers the dates already
 * materialized (the next 14 days) — the rest of the block is spent later as
 * those dates come around, which is not a partial failure.
 */
export const enrollInClass = async (
  classId: string,
  payload: EnrollPayload
): Promise<EnrollResult> => {
  const response = await apiClient.post(
    `/classes/${classId}/enroll`,
    buildEnrollBody(payload)
  );
  return (response.data as any)?.data ?? (response.data as EnrollResult);
};

/**
 * Drop-in: books the client into exactly one session. Passing a `v:` id
 * materializes that session — read the new id off the response with
 * `readMaterializedSessionId` and refetch anything that showed the old one.
 *
 * `clientPackageId` — omit to let the backend auto-pick (category-specific
 * package preferred over a general one), or pass one from
 * `listRedeemablePackagesForSession` to book against a specific purchase.
 */
export const bookSession = async (
  appointmentId: string,
  clientId: string,
  clientPackageId?: string,
  forceDropIn?: boolean
): Promise<any> => {
  const response = await apiClient.post(
    `/classes/sessions/${sessionPath(appointmentId)}/book`,
    {
      client_id: clientId,
      ...(clientPackageId ? { client_package_id: clientPackageId } : {}),
      ...(forceDropIn ? { use_package: false } : {}),
    }
  );
  return response.data;
};

/**
 * Every client_package the client could use for this session — a matching
 * category-scoped package plus any general ALL-scope one, soonest-expiring
 * first. Call this before booking to detect ambiguity: 0 means booking will
 * fall back to a per-session invoice, 1 needs no prompt, >1 should let staff
 * pick which one to redeem.
 */
export const listRedeemablePackagesForSession = async (
  appointmentId: string,
  clientId: string
): Promise<ClientPackage[]> => {
  const response = await apiClient.get(
    `/classes/sessions/${sessionPath(appointmentId)}/redeemable-packages`,
    { params: { client_id: clientId } }
  );
  return unwrapList(response.data, "client_packages", "packages") as ClientPackage[];
};

/** Removes one client from a session's roster (refunds package or invoice). */
export const cancelAttendance = async (
  appointmentId: string,
  clientId: string
): Promise<any> => {
  const response = await apiClient.patch(
    `/classes/sessions/${sessionPath(appointmentId)}/attendance/${clientId}/cancel`
  );
  return response.data;
};

/**
 * Works with a projected id too, and then returns who *will* be on the session
 * — the enrollments with sessions left on their block, capped at capacity.
 * Those entries carry `is_projected: true` and a null `id`.
 */
export const getSessionRoster = async (
  appointmentId: string
): Promise<RosterEntry[]> => {
  const response = await apiClient.get(
    `/classes/sessions/${sessionPath(appointmentId)}/roster`
  );
  return unwrapList(response.data, "roster", "attendees") as RosterEntry[];
};

/**
 * Cancels the whole session and cleans up every attendee. Use this instead of
 * the generic `PATCH /appointments/:id/cancel`, which rejects class sessions.
 * A projected session materializes as CANCELLED, drops out of the projection
 * and frees the court again.
 */
export const cancelSession = async (
  appointmentId: string,
  reason?: string
): Promise<CancelSessionResult> => {
  const response = await apiClient.patch(
    `/classes/sessions/${sessionPath(appointmentId)}/cancel`,
    reason ? { reason } : {}
  );
  return (response.data as any)?.data ?? (response.data as CancelSessionResult);
};
